import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import LiveChatModal from '@/components/chat/LiveChatModal';
import { HelpCircle, MessageCircle, Mail, Phone, Book, Users, ExternalLink, Search, Clock, CheckCircle, AlertCircle } from 'lucide-react';
const faqItems = [{
  question: "How do I upgrade my subscription plan?",
  answer: "You can upgrade your plan anytime from the Subscription tab. Choose your desired plan and follow the checkout process. Your new features will be available immediately.",
  category: "Billing"
}, {
  question: "Can I cancel my subscription at any time?",
  answer: "Yes, you can cancel your subscription anytime through the Subscription tab or customer portal. Your access will continue until the end of your billing period.",
  category: "Billing"
}, {
  question: "What payment methods do you accept?",
  answer: "We accept all major credit cards (Visa, Mastercard, American Express) and debit cards through our secure Stripe payment processing.",
  category: "Billing"
}, {
  question: "How does the packing assistant work?",
  answer: "Our AI-powered packing assistant analyzes your destination, travel dates, weather, and activities to suggest personalized packing lists. Premium users get advanced recommendations.",
  category: "Features"
}, {
  question: "Is my travel data secure?",
  answer: "Yes, we use industry-standard encryption and security measures. Your data is stored securely and never shared with third parties without your consent.",
  category: "Privacy"
}, {
  question: "How accurate is the currency converter?",
  answer: "Our currency converter uses real-time exchange rates updated every few minutes.",
  category: "Features"
}, {
  question: "Can I share my trip plans with friends?",
  answer: "Yes! You can share trip details, packing lists, and expense groups with other users. This feature is available on all subscription tiers.",
  category: "Features"
}, {
  question: "How do I track group expenses?",
  answer: "Create an expense group from your trip dashboard, invite members, and start logging shared expenses. Our smart split calculator helps divide costs fairly.",
  category: "Features"
}, {
  question: "What are boost passes?",
  answer: "Boost passes give you temporary access to premium features for a specific duration (3, 7, or 14 days). Perfect for occasional travelers who don't need a full subscription.",
  category: "Billing"
}, {
  question: "How do I export my travel data?",
  answer: "Premium users can export all their trip data, expenses, and documents through the Data Management section in Account Settings.",
  category: "Account"
}, {
  question: "Can I use OdyVoy offline?",
  answer: "Some features like viewing saved trips and packing lists work offline with the First Class Pack subscription. Features requiring real-time data (currency rates, weather) need an internet connection.",
  category: "Features"
}, {
  question: "How do I reset my password?",
  answer: "Click 'Forgot Password' on the login page. We'll send a reset link to your registered email address. Follow the link to create a new password.",
  category: "Account"
}, {
  question: "What's included in the free Economy plan?",
  answer: "The Economy plan includes basic trip planning, packing lists, visa requirements check, expense tracking, and access to our AI travel assistant with usage limits.",
  category: "Billing"
}, {
  question: "How do I contact customer support?",
  answer: "You can reach us via live chat (available 24/7), email at info@odyvoy.com, or through the contact form in the Support Center.",
  category: "Support"
}, {
  question: "Can I get a refund?",
  answer: "At this time, we do not offer refunds on subscriptions. We encourage you to explore our features using the free Economy plan or try a boost pass before committing to a paid subscription. If you have concerns about your subscription, please contact our support team.",
  category: "Billing"
}];
const supportTickets = [{
  id: "TICKET-001",
  subject: "Payment method not working",
  status: "resolved",
  date: "2024-01-18",
  category: "Billing"
}, {
  id: "TICKET-002",
  subject: "Feature request: offline maps",
  status: "in-progress",
  date: "2024-01-15",
  category: "Feature Request"
}];
export function HelpSection() {
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'in-progress':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'open':
        return <AlertCircle className="h-4 w-4 text-cyan-600" />;
      default:
        return <HelpCircle className="h-4 w-4" />;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'open':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-muted text-foreground';
    }
  };
  return <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">Support Center</h2>
        <p className="text-muted-foreground">Get help, find answers, and connect with our support team</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 hover:border-primary/30 transition-colors cursor-pointer" onClick={() => setIsChatOpen(true)}>
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-primary/10 p-4">
                <MessageCircle className="h-8 w-8 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Live Chat</h3>
                <p className="text-sm text-muted-foreground">Get Instant Help From Our AI Assistant</p>
                <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200">
                  Available Now
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200 hover:border-blue-300 transition-colors">
          <CardContent className="p-6">
            <a href="mailto:info@odyvoy.com?subject=Support Request" className="no-underline">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="rounded-full bg-blue-100 p-4">
                  <Mail className="h-8 w-8 text-cyan-600" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-blue-900">Email</h3>
                  <p className="text-sm text-cyan-700">Send Us a Detailed Message</p>
                  <Badge variant="outline" className="border-blue-300 text-cyan-700">
                    info@odyvoy.com
                  </Badge>
                </div>
              </div>
            </a>
          </CardContent>
        </Card>
      </div>

      {/* Contact Form */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            Contact Support
          </CardTitle>
          <CardDescription>
            Describe your issue and we'll get back to you quickly
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Subject</label>
              <Input placeholder="Brief description of your issue" className="h-10" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <select className="w-full p-2 h-10 border border-input rounded-md bg-background text-sm">
                <option>General Question</option>
                <option>Billing & Payments</option>
                <option>Technical Issue</option>
                <option>Feature Request</option>
                <option>Bug Report</option>
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Message</label>
            <Textarea placeholder="Please describe your issue in detail..." className="min-h-[120px] resize-none" />
          </div>
          <Button className="w-full md:w-auto h-10">
            <Mail className="h-4 w-4 mr-2" />
            Send Message
          </Button>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <Book className="h-5 w-5 text-primary" />
            Frequently Asked Questions
          </CardTitle>
          <CardDescription>
            Quick answers to common questions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search FAQ..." className="border-0 bg-transparent shadow-none focus-visible:ring-0 h-8" />
          </div>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => <div key={index} className="p-4 rounded-lg border bg-card hover:bg-muted/20 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <h4 className="font-medium text-sm leading-relaxed flex-1">{item.question}</h4>
                  <Badge variant="outline" className="text-xs flex-shrink-0">{item.category}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{item.answer}</p>
              </div>)}
          </div>

          <Button variant="outline" className="w-full h-10" onClick={() => navigate('/help')}>
            <ExternalLink className="h-4 w-4 mr-2" />
            View Complete FAQ
          </Button>
        </CardContent>
      </Card>

      <LiveChatModal open={isChatOpen} onOpenChange={setIsChatOpen} />
    </div>;
}